'use client';

import { useLoginData } from '@/hooks/auth/sso/useLoginData';
import { useLoginForm } from '@/hooks/auth/local/useLoginForm';
import { getContentstackAuthorizeUrl, getOAuthRedirectUri } from '@/lib/auth/idp-urls';
import { getAndClearLoginModalRole } from '@/utils/auth/common/auth-storage';
import { deleteQueryParam } from '@/lib/utils/url-params';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ModalCloseButton } from '../../common/modal-close-button';
import MaterialIcons from '../../MaterialIcons/materialIcons';
import { DialogTitle } from '../../ui/dialog';
import { EmailPasswordLoginForm } from '../local/EmailPasswordLoginForm';
import { ResetPasswordModal } from '../local/ResetPasswordModal';
import { PartnerLoginSection } from '../saml/PartnerLoginSection';
import { RoleSelector } from './RoleSelector';
import { SSOSection } from '../sso/SSOSection';

interface LoginModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onRegisterClick?: () => void;
  /** Message from whoever rendered this (e.g. <SsoCallbackHandler/> reporting a failed exchange) — this component doesn't run the callback itself. */
  notice?: string | null;
}

export function LoginModal({
  isOpen = false,
  onClose,
  onRegisterClick,
  notice = null,
}: LoginModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'member' | 'partner' | ''>('');
  const [roleError, setRoleError] = useState<string>('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>('');

  const searchParams = useSearchParams();
  const roleFromParams = searchParams?.get('role');
  const sessionExpired = searchParams?.get('session_expired') === '1';
  const partnerLoginError = searchParams?.get('partner_login_error') === '1';
  // Correlation id for the SSO failure, shown so support has something to look up. Safe to
  // read here even after the effect below strips it via history.replaceState, which bypasses
  // the router and so doesn't update useSearchParams.
  const partnerLoginRef = searchParams?.get('ref') ?? null;

  // Custom hooks
  const { countryLoginItems, uniqueRegions, isLoading: isLoadingSSOData } = useLoginData();
  const { loading, errors, errorMsg, handleLogin, clearErrors } = useLoginForm();

  // Clear partner_login_error (and its ref) from the URL once we've captured them, so the
  // URL is clean when the user closes the modal and a reload does not re-show the error.
  useEffect(() => {
    if (!partnerLoginError) return;
    deleteQueryParam('partner_login_error');
    deleteQueryParam('ref');
  }, [partnerLoginError]);

  // Set role from URL params or from sessionStorage when modal opens (e.g. from partner-only overlay)
  useEffect(() => {
    if (!isOpen) return;
    const roleFromStorage = getAndClearLoginModalRole();
    const nextRole = roleFromStorage || (roleFromParams === 'partner' || roleFromParams === 'member' ? roleFromParams : null);
    if (nextRole) {
      setRole(nextRole);
      setRoleError('');
    }
  }, [isOpen, roleFromParams]);

  // Default region ("North America" preferred), derived directly rather than synced via an
  // effect; selectedRegion only holds the user's explicit override.
  const defaultRegion = useMemo(
    () => uniqueRegions.find(region => region === 'North America') || uniqueRegions[0] || '',
    [uniqueRegions]
  );
  const effectiveSelectedRegion = selectedRegion || defaultRegion;

  // Handlers
  const handleEmailPasswordLogin = useCallback(async () => {
    // Validate role is selected
    if (!role) {
      setRoleError('Registration type is required');
      return;
    }
    setRoleError('');
    await handleLogin(email, password);
  }, [role, handleLogin, email, password]);

  const handleRoleChange = useCallback((newRole: 'member' | 'partner') => {
    setRole(newRole);
    setRoleError('');
  }, []);

  const handleSSOLogin = useCallback((regionCode: string) => {
    // getOAuthRedirectUri() must match the return leg's value byte-for-byte, or the IdP rejects the exchange.
    window.location.href = getContentstackAuthorizeUrl(regionCode, getOAuthRedirectUri());
  }, []);

  const openResetPassword = useCallback(() => setShowResetPassword(true), []);

  return (
    <>
      {!showResetPassword && (
        <div className="login-modal-container">
          <ModalCloseButton onClick={onClose} />
          <div className="login-modal-content">
          {/* Header Row */}
          <div className="flex items-center justify-between gap-2 sm:gap-4 mb-6">
            <DialogTitle className="cs-h3 lg:h1-compact min-w-0 truncate">Login options</DialogTitle>
            <button
              onClick={onRegisterClick}
              className="group  flex items-center gap-2 transition-colors flex-shrink-0 whitespace-nowrap"
            >
              <span className="additional-type-s-button p-0.5">Register Now</span>
              <MaterialIcons iconName="arrow_right_alt" className="text-icon-subtle btn-icon !text-[16px] flex-shrink-0 leading-none transition-transform duration-300 ease-in-out group-hover:translate-x-1" />
            </button>
          </div>

          {/* Session expired message – when redirected after invalid cs_refresh_token */}
          {sessionExpired && (
            <div className="mb-4 p-3 rounded-md bg-surface-2 border border-border-light">
              <p className="cs-s-body text-text-default-medium">Your session has expired. Please log in again.</p>
            </div>
          )}

          {/* Partner SSO failed – redirected here by PartnerSessionSync; reason is server-side under `ref`. */}
          {partnerLoginError && (
            <div className="mb-4 p-3 rounded-md bg-surface-2 border border-border-light">
              <p className="cs-s-body text-text-default-medium">Partner login failed. Please try Log In As Partner again.</p>
              {partnerLoginRef && (
                <p className="cs-s-body text-text-default-medium mt-1">
                  If it keeps failing, quote reference <span className="font-mono">{partnerLoginRef}</span> to support.
                </p>
              )}
            </div>
          )}

          {/* Whatever opened this modal wanted said — e.g. a failed SSO exchange on /login. */}
          {notice && (
            <div className="my-1">
              <p className="cs-error">{notice}</p>
            </div>
          )}

          <SSOSection
            countryLoginItems={countryLoginItems}
            uniqueRegions={uniqueRegions}
            selectedRegion={effectiveSelectedRegion}
            onRegionChange={setSelectedRegion}
            onSSOClick={handleSSOLogin}
            isLoading={isLoadingSSOData}
            dividerText="or Login with Email & Password"
          />

          {/* Role Selection */}
          <RoleSelector role={role} onRoleChange={handleRoleChange} error={roleError} />

          {/* Partner Login Section - Show when partner role is selected */}
          {role === 'partner' ? (
            <PartnerLoginSection />
          ) : (
            /* Member Login - Show when member role is selected */
            <EmailPasswordLoginForm
            email={email}
            password={password}
            errors={errors}
            errorMsg={errorMsg}
            roleError={roleError}
            loading={loading}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onLogin={handleEmailPasswordLogin}
            onResetPasswordClick={openResetPassword}
            onClearErrors={clearErrors}
          />
          )}
        </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="relative z-[101]">
            <ResetPasswordModal onClose={() => setShowResetPassword(false)} />
          </div>
        </div>
      )}

    </>
  );
}
